import {
  ArrowRightEndOnRectangleIcon,
  EnvelopeIcon,
  LinkSlashIcon,
  PlusIcon,
  TrashIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import type { ColDef } from 'ag-grid-community';
import type { CustomCellRendererProps } from 'ag-grid-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { UserContext } from '~/app';
import { Pill } from '~/components';
import {
  formatInvitationStatus,
  InvitationActionLinks,
  InvitationStatusPill,
} from '~/components/agGrid/columns/invitationColumns';
import { BasicGrid } from '~/components/agGrid/Grid';
import { Button } from '~/components/buttons/Button/Button';
import { LoadingSpinner } from '~/components/LoadingSpinner';
import { ACTIONS_COLUMN_BASE } from '~/components/Table/gridUtil';
import type { OnShowAlert } from '~/features/alerts';
import { useProviderAssociationRef } from '~/features/market/marketSelectors';
import {
  MarketContentPanel,
  MarketEmptyPanel,
  MarketPanelHeader,
  MarketSegmentedControl,
} from '~/features/shared/market-content';
import { TutorialVideoButton } from '~/features/tutorials/TutorialVideoButton';
import { buildEnterImpersonationUrl } from '~/features/user/impersonationHandoff';
import { dateStringFormatter } from '~/features/utils';
import type {
  AssociationEndReason,
  AssociationHistoryEntry,
  ProviderEmployerAssociation,
} from '~/types';
import { AddManagedProviderModal } from './AddManagedProviderModal';
import { InviteProviderModal } from './InviteProviderModal';
import { ManagedInviteModal } from './ManagedInviteModal';
import type { ToastNotice } from './providerAssociation.machine';

const TAGS = CT.employer.providerAssociations;
const TAB_TAGS = TAGS.tabs;

type AssociationTab = 'associations' | 'invitations' | 'previous';

// Tab position <-> tab, as the machine stores it (tabIndex).
const TAB_INDEX: Record<AssociationTab, number> = { associations: 0, invitations: 1, previous: 2 };

const TAB_OPTIONS: { label: string; value: AssociationTab; cyTag: string }[] = [
  { label: 'Associated Providers', value: 'associations', cyTag: TAB_TAGS.associatedProvidersTab },
  { label: 'Invitations', value: 'invitations', cyTag: TAB_TAGS.invitationsTab },
  { label: 'Previous Providers', value: 'previous', cyTag: TAB_TAGS.previousProvidersTab },
];

// [MCV-1927] How an association ended, as the Previous Providers tab words it.
export const END_REASON_LABELS: Record<AssociationEndReason, string> = {
  EMPLOYER_REMOVED: 'Removed by your organization',
  PROVIDER_DISASSOCIATED: 'Provider disassociated',
  MANAGED_VAULT_ARCHIVED: 'Removed before accepting',
};

// [MCV-1927] The grid's default sort/filter read each column's field, so every column here is
// bound to its data (a renderer alone leaves the value empty). Dates are ISO strings, so the
// default string sort is chronological; the text filter matches the date as it is shown.
const historyDate = (value: string | null | undefined): string =>
  value ? dateStringFormatter(value, 'SHORT_DATE') : '';

const historyEndReasonLabel = (entry?: AssociationHistoryEntry): string =>
  entry ? (END_REASON_LABELS[entry.endReason] ?? entry.endReason) : '';

// Exported for the Previous Providers tab tests.
export const createHistoryColumns = (): ColDef[] => [
  {
    field: 'providerDisplayName',
    headerName: 'Provider',
    flex: 2,
    cellRenderer: ({ data }: CustomCellRendererProps<AssociationHistoryEntry>) => (
      <span className="inline-flex items-center gap-2">
        {data?.providerDisplayName}
        {data?.wasEmployerManaged && (
          <Pill label="Employer-managed" type="info" cyTag={TAGS.list.historyManagedPill} />
        )}
      </span>
    ),
  },
  {
    field: 'providerTypeDescription',
    headerName: 'Type',
    valueFormatter: ({ value }) => value || '—',
    filterValueGetter: ({ data }) => data?.providerTypeDescription ?? '',
  },
  {
    field: 'associatedAt',
    headerName: 'Associated',
    valueFormatter: ({ value }) => historyDate(value),
    filterValueGetter: ({ data }) => historyDate(data?.associatedAt),
  },
  {
    field: 'endedAt',
    headerName: 'Ended',
    // Newest first until the user picks another sort; initialSort is applied once, so a
    // re-render with a fresh colDefs array does not reset their choice.
    initialSort: 'desc',
    valueFormatter: ({ value }) => historyDate(value),
    filterValueGetter: ({ data }) => historyDate(data?.endedAt),
  },
  {
    colId: 'endReason',
    headerName: 'How it ended',
    flex: 2,
    valueGetter: ({ data }: { data?: AssociationHistoryEntry }) => historyEndReasonLabel(data),
  },
];

// Exported for ActionsColumnPinning tests — the Actions column below spreads
// ACTIONS_COLUMN_BASE (components/Table/gridUtil.tsx).
export const createInvitationColumns = (
  onRevoke: (id: number) => void,
  onResend: (id: number) => void,
  disabled = false
): ColDef[] => [
  {
    field: 'invitationEmail',
    headerName: 'Email',
  },
  {
    headerName: 'Invited By',
    cellRenderer: ({ data }: CustomCellRendererProps) =>
      data.invitedBy
        ? [data.invitedBy.firstName, data.invitedBy.lastName].filter(Boolean).join(' ')
        : '—',
  },
  {
    headerName: 'Date',
    cellRenderer: ({ data }: CustomCellRendererProps) =>
      dateStringFormatter(data.invitedAt, 'SHORT_DATE'),
  },
  {
    headerName: 'Status',
    // [MCV-1872] This column has no field, so the default text filter had no
    // value to match; filter on the label the pill shows ('Email Failed').
    filterValueGetter: ({ data }) => formatInvitationStatus(data?.status),
    cellRenderer: ({ data }: CustomCellRendererProps) => (
      <InvitationStatusPill status={data.status} />
    ),
  },
  {
    // [MCV-1405] Pinning, locking and [MCV-1750] resizable:false come from ACTIONS_COLUMN_BASE.
    ...ACTIONS_COLUMN_BASE,
    width: 170,
    cellRenderer: ({ data }: CustomCellRendererProps) => {
      const pending = data.status === 'PENDING';

      return (
        <InvitationActionLinks
          onResend={pending ? () => onResend(data.id) : undefined}
          onRevoke={pending ? () => onRevoke(data.id) : undefined}
          disabled={disabled}
          resendCyTag={TAGS.list.resendBtn}
          revokeCyTag={TAGS.list.revokeBtn}
        />
      );
    },
  },
];

const buildAssociationSearchable = (assoc: ProviderEmployerAssociation): string =>
  [
    assoc.provider.prefixName,
    assoc.provider.firstName,
    assoc.provider.lastName,
    assoc.provider.npiNum,
    assoc.provider.category?.description,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

const providerDisplayName = (assoc: ProviderEmployerAssociation) =>
  [assoc.provider.prefixName, assoc.provider.firstName, assoc.provider.lastName]
    .filter(Boolean)
    .join(' ') || 'No Name Given';

// [MCV-1894] An employer-managed vault has no Clerk account yet.
export const isEmployerManaged = (assoc: ProviderEmployerAssociation) =>
  assoc.provider.managedByEmployerId != null;

type EnterVaultDeps = {
  userRef: {
    send: (event: {
      type: 'ON_START_IMPERSONATION';
      providerId: number;
      providerName: string;
    }) => void;
  };
  navigate: (to: string) => void;
};

const enterVault = (
  { userRef, navigate }: EnterVaultDeps,
  providerId: number,
  providerName: string
) => {
  const enterVaultUrl = buildEnterImpersonationUrl({ providerId, providerName });

  if (enterVaultUrl.startsWith('https://')) {
    window.location.assign(enterVaultUrl);
    return;
  }

  userRef.send({ type: 'ON_START_IMPERSONATION', providerId, providerName });
  navigate(enterVaultUrl);
};

type ToastHandlers = {
  onEnterVault: (providerId: number, providerName: string) => void;
  onResendInvitation: (invitationId: number) => void;
};

// [MCV-1894] The toast after Add Provider / Send invite. When the invitation email
// did not send (emailSent false = failed, null = uncertain) the vault or invitation
// still exists, so the toast offers a resend of THAT invitation.
export const buildVaultToast = (
  notice: ToastNotice,
  { onEnterVault, onResendInvitation }: ToastHandlers
): OnShowAlert['payload'] => {
  const { invitation, providerId, providerName, kind } = notice;
  const emailFailed = invitation != null && invitation.emailSent !== true;
  const actions: NonNullable<OnShowAlert['payload']['actions']> = [];

  if (invitation != null && emailFailed) {
    actions.push({
      label: 'Resend invitation',
      cyTag: TAGS.toast.resendInviteBtn,
      onClick: () => onResendInvitation(invitation.id),
    });
  }
  if (kind === 'added' && providerId != null) {
    actions.push({
      label: 'Enter Vault',
      cyTag: TAGS.toast.enterVaultBtn,
      onClick: () => onEnterVault(providerId, providerName),
    });
  }

  if (emailFailed) {
    return {
      type: 'warning',
      text:
        kind === 'added'
          ? 'Vault created; invitation email did not send'
          : 'Invitation created; email did not send',
      subText:
        invitation.emailSent === false
          ? `The invitation for ${providerName} was saved but could not be delivered. Resend it to try again.`
          : `We could not confirm delivery of the invitation for ${providerName}. Resend it if they do not receive it.`,
      timeout: 15000,
      cyTag: TAGS.toast.emailFailed,
      actions,
    };
  }
  return {
    type: 'success',
    text: kind === 'added' ? 'Provider vault created' : 'Invitation sent',
    subText:
      kind === 'added'
        ? invitation
          ? `${providerName} was added and an invitation was emailed.`
          : `${providerName} was added.`
        : `${providerName} was invited to claim their vault.`,
    timeout: 8000,
    cyTag: TAGS.toast.created,
    actions,
  };
};

type AssociationCardProps = {
  assoc: ProviderEmployerAssociation;
  isAccountOwner: boolean;
  mutating: boolean;
  onEnterVault: (assoc: ProviderEmployerAssociation) => void;
  onDisassociate: (assoc: ProviderEmployerAssociation) => void;
  onRemoveManaged: (assoc: ProviderEmployerAssociation) => void;
  onSendManagedInvite: (assoc: ProviderEmployerAssociation) => void;
};

// Exported for the card tests (managed vs claimed rows).
export const AssociationCard = ({
  assoc,
  isAccountOwner,
  mutating,
  onEnterVault,
  onDisassociate,
  onRemoveManaged,
  onSendManagedInvite,
}: AssociationCardProps) => {
  const managed = isEmployerManaged(assoc);
  return (
    <div
      data-cy={TAGS.list.associationCard}
      className="flex items-center justify-between px-5 py-4 sm:px-6"
    >
      <div className="flex min-w-0 items-center gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-700 text-sm font-bold text-white">
          {(assoc.provider.firstName?.[0] ?? '').toUpperCase()}
          {(assoc.provider.lastName?.[0] ?? '').toUpperCase()}
        </div>
        <div className="min-w-0 text-left">
          <p className="font-medium text-content-primary">
            {[assoc.provider.prefixName, assoc.provider.firstName, assoc.provider.lastName]
              .filter(Boolean)
              .join(' ')}
          </p>
          <p className="text-xs text-content-secondary">
            {assoc.provider.npiNum && `NPI: ${assoc.provider.npiNum}`}
            {assoc.provider.npiNum && assoc.provider.category?.description && ' · '}
            {assoc.provider.category?.description}
          </p>
          <p className="text-xs text-content-tertiary">
            Associated {dateStringFormatter(assoc.associatedAt, 'SHORT_DATE')}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {managed && (
          <Pill label="Employer-managed" type="info" cyTag={TAGS.list.employerManagedPill} />
        )}
        <Pill label="Active" type="success" />
        {managed && (
          <Button
            cyTag={TAGS.list.sendManagedInviteBtn}
            type="button"
            colorVariant="secondary"
            disabled={mutating}
            onClick={() => onSendManagedInvite(assoc)}
          >
            <span className="inline-flex items-center gap-2">
              <EnvelopeIcon className="h-4 w-4" />
              Send invite
            </span>
          </Button>
        )}
        <Button
          cyTag={TAGS.list.enterVaultBtn}
          type="button"
          colorVariant="tertiary"
          onClick={() => onEnterVault(assoc)}
        >
          <span className="inline-flex items-center gap-2">
            <ArrowRightEndOnRectangleIcon className="h-4 w-4" />
            Enter Vault
          </span>
        </Button>
        {/* [MCV-1927] An unclaimed managed vault is removed (archived), not disassociated. */}
        {isAccountOwner && managed && (
          <Button
            cyTag={TAGS.list.removeBtn}
            type="button"
            colorVariant="error"
            disabled={mutating}
            onClick={() => onRemoveManaged(assoc)}
          >
            <span className="inline-flex items-center gap-2">
              <TrashIcon className="h-4 w-4" />
              Remove
            </span>
          </Button>
        )}
        {isAccountOwner && !managed && (
          <Button
            cyTag={TAGS.list.disassociateBtn}
            type="button"
            colorVariant="error"
            disabled={mutating}
            onClick={() => onDisassociate(assoc)}
          >
            <span className="inline-flex items-center gap-2">
              <LinkSlashIcon className="h-4 w-4" />
              Disassociate
            </span>
          </Button>
        )}
      </div>
    </div>
  );
};

export const ProviderAssociationList = () => {
  const { loading, employerUser, providerAssociationRef } = useProviderAssociationRef();
  const userRef = UserContext.useActorRef();
  const navigate = useNavigate();
  const context = useSelector(providerAssociationRef, (state) => state?.context);
  const isSending = useSelector(
    providerAssociationRef,
    (state) => state?.matches('$_SENDING_INVITE') ?? false
  );
  const isPendingExists = useSelector(
    providerAssociationRef,
    (state) => state?.matches('$_PENDING_EXISTS') || state?.matches('$_RESENDING_PENDING') || false
  );
  const isResendingPending = useSelector(
    providerAssociationRef,
    (state) => state?.matches('$_RESENDING_PENDING') ?? false
  );
  const isAddModalOpen = useSelector(
    providerAssociationRef,
    (state) =>
      state?.matches('$_ADD_MODAL_OPEN') ||
      state?.matches('$_LOOKING_UP_NPI') ||
      state?.matches('$_ADDING_MANAGED_PROVIDER') ||
      false
  );
  const isAdding = useSelector(
    providerAssociationRef,
    (state) => state?.matches('$_ADDING_MANAGED_PROVIDER') ?? false
  );
  const isSendingManagedInvite = useSelector(
    providerAssociationRef,
    (state) => state?.matches('$_SENDING_MANAGED_INVITE') ?? false
  );
  const canOpenAddModal = useSelector(
    providerAssociationRef,
    (state) => state?.matches('$_VIEW') ?? false
  );
  const isMutatingInvitation = useSelector(
    providerAssociationRef,
    (state) =>
      state?.matches('$_RESENDING_INVITE') ||
      state?.matches('$_REVOKING_INVITE') ||
      state?.matches('$_REMOVING_ASSOCIATION') ||
      state?.matches('$_SENDING_MANAGED_INVITE') ||
      false
  );
  const employerId = employerUser?.employerId;
  // [MCV-1826] Only the account owner may disassociate a provider; the server
  // enforces the same rule. An employer with no recorded owner (legacy rows)
  // lets any member, since neither side can tell them apart.
  const primaryUserIdAdmin = employerUser?.employer?.primaryUserIdAdmin;
  const isAccountOwner =
    employerUser != null &&
    (primaryUserIdAdmin == null || employerUser.userInfoId === primaryUserIdAdmin);
  const [search, setSearch] = useState('');
  const toastNotice = context?.toastNotice ?? null;

  useEffect(() => {
    if (!providerAssociationRef || !employerId) return;

    const snapshot = providerAssociationRef.getSnapshot();
    const isBusy =
      snapshot.matches('$_LOADING') ||
      snapshot.matches('$_SENDING_INVITE') ||
      snapshot.matches('$_PENDING_EXISTS') ||
      snapshot.matches('$_RESENDING_PENDING') ||
      snapshot.matches('$_RESENDING_INVITE') ||
      snapshot.matches('$_REVOKING_INVITE') ||
      snapshot.matches('$_REMOVING_ASSOCIATION') ||
      snapshot.matches('$_LOOKING_UP_INVITE_NPI') ||
      snapshot.matches('$_ADD_MODAL_OPEN') ||
      snapshot.matches('$_LOOKING_UP_NPI') ||
      snapshot.matches('$_ADDING_MANAGED_PROVIDER') ||
      snapshot.matches('$_SENDING_MANAGED_INVITE');
    if (snapshot.context.employerId !== employerId && !isBusy) {
      providerAssociationRef.send({ type: 'ON_LOAD', payload: { employerId } });
    }
  }, [employerId, providerAssociationRef]);

  // [MCV-1894] Turn the machine's "vault created / invite sent" notice into a toast.
  useEffect(() => {
    if (!toastNotice || !providerAssociationRef) return;
    // Guard a repeated effect run (StrictMode): show each notice once.
    if (providerAssociationRef.getSnapshot().context.toastNotice !== toastNotice) return;

    userRef.send({
      type: 'ON_SHOW_ALERT',
      payload: buildVaultToast(toastNotice, {
        onEnterVault: (providerId, providerName) =>
          enterVault({ userRef, navigate }, providerId, providerName),
        onResendInvitation: (invitationId) =>
          providerAssociationRef.send({ type: 'ON_RESEND_INVITATION', invitationId }),
      }),
    });
    providerAssociationRef.send({ type: 'ON_TOAST_NOTICE_HANDLED' });
  }, [toastNotice, providerAssociationRef, userRef, navigate]);

  const associations = context?.associations ?? [];
  const filteredAssociations = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return associations;
    return associations.filter((assoc) => buildAssociationSearchable(assoc).includes(q));
  }, [associations, search]);

  if (loading) {
    return <LoadingSpinner />;
  }
  if (!providerAssociationRef || !context) return null;

  const {
    invitations,
    history,
    historyError,
    modalOpen,
    inviteEmail,
    inviteValidationError,
    inviteNpi,
    inviteNpiError,
    inviteNpiMatch,
    addForm,
    addValidationError,
    addNpiError,
    addSubmitFailures,
    npiLookup,
    addConflict,
    managedInvite,
    tabIndex,
  } = context;
  const activeTab: AssociationTab =
    tabIndex === TAB_INDEX.previous
      ? 'previous'
      : tabIndex === TAB_INDEX.invitations
        ? 'invitations'
        : 'associations';

  const handleRevoke = (invitationId: number) => {
    providerAssociationRef.send({ type: 'ON_SEND_REVOKE_CONFIRMATION', invitationId });
  };

  const handleResend = (invitationId: number) => {
    providerAssociationRef.send({ type: 'ON_SEND_RESEND_CONFIRMATION', invitationId });
  };

  const handleDisassociate = (assoc: ProviderEmployerAssociation) => {
    providerAssociationRef.send({
      type: 'ON_SEND_REMOVE_ASSOCIATION_CONFIRMATION',
      associationId: assoc.id,
      providerName: providerDisplayName(assoc),
    });
  };

  const handleRemoveManaged = (assoc: ProviderEmployerAssociation) => {
    providerAssociationRef.send({
      type: 'ON_SEND_REMOVE_ASSOCIATION_CONFIRMATION',
      associationId: assoc.id,
      providerName: providerDisplayName(assoc),
      managed: true,
    });
  };

  const handleEnterVault = (assoc: ProviderEmployerAssociation) =>
    enterVault({ userRef, navigate }, assoc.provider.id, providerDisplayName(assoc));

  const handleSendManagedInvite = (assoc: ProviderEmployerAssociation) => {
    providerAssociationRef.send({
      type: 'ON_OPEN_MANAGED_INVITE',
      associationId: assoc.id,
      providerName: providerDisplayName(assoc),
    });
  };

  const handleTabChange = (next: AssociationTab) => {
    providerAssociationRef.send({
      type: 'ON_SET_TAB_INDEX',
      payload: { index: TAB_INDEX[next] },
    });
  };

  const addButton = (
    <Button
      cyTag={TAGS.list.addBtn}
      type="button"
      colorVariant="secondary"
      disabled={!canOpenAddModal}
      onClick={() => providerAssociationRef.send({ type: 'ON_OPEN_ADD_MODAL' })}
    >
      <span className="inline-flex items-center gap-2">
        <UserPlusIcon className="h-4 w-4" />
        Add Provider
      </span>
    </Button>
  );

  const inviteButton = (
    <Button
      cyTag={TAGS.list.inviteBtn}
      type="button"
      colorVariant="primary"
      onClick={() => providerAssociationRef.send({ type: 'ON_OPEN_MODAL' })}
    >
      <span className="inline-flex items-center gap-2">
        <PlusIcon className="h-4 w-4" />
        Invite Provider
      </span>
    </Button>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-cy="provider-associations">
      <MarketContentPanel
        bodyClassName="p-0"
        header={
          <div className="flex flex-col gap-3">
            <MarketPanelHeader
              title="My Providers"
              subtitle="Manage your associated providers and pending invitations."
              actions={
                <div className="flex flex-wrap items-center gap-3">
                  <TutorialVideoButton
                    videoId="addAssociatedProviders"
                    tooltipAlign="start-then-end"
                  />
                  {addButton}
                  {inviteButton}
                </div>
              }
            />
            <MarketSegmentedControl
              options={TAB_OPTIONS}
              value={activeTab}
              onChange={handleTabChange}
            />
            {activeTab === 'associations' && (
              <input
                type="text"
                placeholder="Search providers..."
                aria-label="Search providers"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-md border border-grey-900 px-3 py-2 text-sm outline-none focus:border-primary-50"
                data-cy={TAGS.list.searchInput}
              />
            )}
          </div>
        }
      >
        {activeTab === 'associations' &&
          (filteredAssociations.length === 0 ? (
            <div data-cy={TAGS.list.emptyState} className="px-6 py-12">
              <MarketEmptyPanel
                title={
                  associations.length === 0
                    ? 'No providers associated yet'
                    : 'No providers match your search'
                }
                description={
                  associations.length === 0
                    ? 'Send an invitation to get started.'
                    : 'Try adjusting your search.'
                }
              />
            </div>
          ) : (
            <div className="divide-y divide-grey-900">
              {filteredAssociations.map((assoc) => (
                <AssociationCard
                  key={assoc.id}
                  assoc={assoc}
                  isAccountOwner={isAccountOwner}
                  mutating={isMutatingInvitation}
                  onEnterVault={handleEnterVault}
                  onDisassociate={handleDisassociate}
                  onRemoveManaged={handleRemoveManaged}
                  onSendManagedInvite={handleSendManagedInvite}
                />
              ))}
            </div>
          ))}

        {activeTab === 'invitations' && (
          <BasicGrid
            data={invitations}
            loading={false}
            colDefs={createInvitationColumns(handleRevoke, handleResend, isMutatingInvitation)}
            pagination={true}
            cyTag={TAGS.list.invitationRow}
          />
        )}

        {activeTab === 'previous' &&
          (historyError ? (
            <div data-cy={TAGS.list.historyError} className="px-6 py-12">
              <MarketEmptyPanel
                title="Couldn't load previous providers."
                action={
                  <Button
                    cyTag={TAGS.list.historyRetryBtn}
                    type="button"
                    colorVariant="secondary"
                    onClick={() => providerAssociationRef.send({ type: 'ON_DATA_CHANGED' })}
                  >
                    Retry
                  </Button>
                }
              />
            </div>
          ) : history.length === 0 ? (
            <div data-cy={TAGS.list.historyEmptyState} className="px-6 py-12">
              <MarketEmptyPanel title="No previous providers yet." />
            </div>
          ) : (
            <BasicGrid
              data={history}
              loading={false}
              colDefs={createHistoryColumns()}
              pagination={true}
              cyTag={TAGS.list.historyRow}
            />
          ))}
      </MarketContentPanel>

      <InviteProviderModal
        open={modalOpen}
        sending={isSending}
        email={inviteEmail}
        validationError={inviteValidationError}
        npi={inviteNpi}
        npiError={inviteNpiError}
        npiMatch={inviteNpiMatch}
        pendingExists={isPendingExists}
        resendingPending={isResendingPending}
        onClose={() => providerAssociationRef.send({ type: 'ON_CLOSE_MODAL' })}
        onEmailChange={(email) =>
          providerAssociationRef.send({ type: 'ON_SET_INVITE_EMAIL', email })
        }
        onNpiChange={(npi) => providerAssociationRef.send({ type: 'ON_SET_INVITE_NPI', npi })}
        onNpiBlur={() => providerAssociationRef.send({ type: 'ON_INVITE_NPI_BLUR' })}
        onAddInstead={() =>
          providerAssociationRef.send({
            type: 'ON_OPEN_ADD_MODAL',
            prefill: { email: inviteEmail.trim(), npiNum: inviteNpi.trim() },
          })
        }
        onSend={() => {
          providerAssociationRef.send({ type: 'ON_SEND_INVITATION' });
        }}
        onResendPending={() => providerAssociationRef.send({ type: 'ON_RESEND_PENDING' })}
        onCancelPending={() => providerAssociationRef.send({ type: 'ON_CANCEL_PENDING' })}
      />

      <AddManagedProviderModal
        open={isAddModalOpen}
        submitting={isAdding}
        form={addForm}
        validationError={addValidationError}
        npiError={addNpiError}
        submitFailures={addSubmitFailures}
        npiLookup={npiLookup}
        conflict={addConflict}
        onClose={() => providerAssociationRef.send({ type: 'ON_CLOSE_ADD_MODAL' })}
        onFieldChange={(field, value) =>
          providerAssociationRef.send({ type: 'ON_SET_ADD_FIELD', field, value })
        }
        onNpiBlur={() => providerAssociationRef.send({ type: 'ON_NPI_BLUR' })}
        onSubmit={() => providerAssociationRef.send({ type: 'ON_SUBMIT_ADD' })}
        onSwitchToInvite={() => providerAssociationRef.send({ type: 'ON_SWITCH_TO_INVITE' })}
      />

      <ManagedInviteModal
        open={managedInvite != null}
        sending={isSendingManagedInvite}
        providerName={managedInvite?.providerName ?? ''}
        email={managedInvite?.email ?? ''}
        error={managedInvite?.error ?? null}
        onClose={() => providerAssociationRef.send({ type: 'ON_CLOSE_MANAGED_INVITE' })}
        onEmailChange={(email) =>
          providerAssociationRef.send({ type: 'ON_SET_MANAGED_INVITE_EMAIL', email })
        }
        onSend={() => providerAssociationRef.send({ type: 'ON_SUBMIT_MANAGED_INVITE' })}
        onSendNormalInvite={() =>
          providerAssociationRef.send({ type: 'ON_SWITCH_MANAGED_TO_INVITE' })
        }
      />
    </div>
  );
};
